import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Select, MenuItem, TextField,
    InputLabel, FormControl, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import { Add, Description, Refresh } from '@mui/icons-material';
import axios from "axios";

// Updated interface to match the API response format
interface ApiBusinessRoom {
    room_id: number;
    title: string;
    host_name: string;
    host_phone: string;
    business_name?: string;
    business_number?: string;
    room_type: string;
    room_status: string;
    room_type_label: string;
    room_status_label: string;
    is_verified: boolean;
    created_at: string;
    created_at_formatted: string;
    business_license_file?: string | null;
    business_identification_file?: string | null;
    verification_status: string;
    is_confirmed: boolean;
    is_rejected: boolean;
    is_active: boolean;
}

// Keep the existing interface for internal component usage
interface BusinessRoom {
    id: string;
    title: string;
    hostName: string;
    hostPhone: string;
    businessName?: string;
    businessNumber?: string;
    roomType: string;
    roomStatus: string;
    isVerified: boolean;
    createdAt: string;
    businessLicenseFile?: string;
    businessIdentificationFile?: string;
    // isConfirmed: boolean;
    // isRejected: boolean;
    // isActive: boolean;
}

interface BusinessRoomStats {
    pending: number;
    reviewing: number;
    active: number;
    verificationRate: string;
    total: number;
}

const statusBadgeColor = (status: string) => {
    switch (status) {
        case '대기중': return 'warning.main';
        case 'PENDING': return 'warning.main';
        case '검토중': return 'info.main';
        case 'REVIEWING': return 'info.main';
        case '활성화': return 'success.main';
        case 'ACTIVE': return 'success.main';
        default: return 'text.secondary';
    }
};
const getRoomStatusLabel = (
    isConfirmed: boolean,
    isRejected: boolean,
    isActive: boolean
): string => {
    if (isRejected) return '승인 거절';
    if (!isConfirmed) return '승인 대기';
    return isActive ? '활성화' : '비활성화';
};
// Transform API data to match component interface
const transformApiData = (apiData: ApiBusinessRoom): BusinessRoom => {
    return {
        id: apiData.room_id.toString(),
        title: apiData.title,
        hostName: apiData.host_name,
        hostPhone: apiData.host_phone,
        businessName: apiData.business_name,
        businessNumber: apiData.business_number,
        roomType: apiData.room_type_label || apiData.room_type,
        roomStatus: getRoomStatusLabel(
            apiData.is_confirmed,
            apiData.is_rejected,
            apiData.is_active
        ),
        isVerified: apiData.is_verified,
        createdAt: apiData.created_at_formatted || apiData.created_at.substring(0, 10),
        businessLicenseFile: apiData.business_license_file ?? '',
        businessIdentificationFile: apiData.business_identification_file?? ''
    };
};

const BusinessRoomsList = () => {
    const [rooms, setRooms] = useState<BusinessRoom[]>([]);
    const [stats, setStats] = useState<BusinessRoomStats>({
        pending: 0,
        reviewing: 0,
        active: 0,
        verificationRate: '0%',
        total: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // 필터 상태
    const [statusFilter, setStatusFilter] = useState('');
    const [roomTypeFilter, setRoomTypeFilter] = useState('');
    const [verificationFilter, setVerificationFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // 문서 모달
    const [docDialogOpen, setDocDialogOpen] = useState(false);
    const [docImage, setDocImage] = useState('');
    const [docTitle, setDocTitle] = useState('');

    // 필터 적용된 API 호출 URL 생성
    const getFilteredApiUrl = () => {
        let url = 'https://roomi.co.kr/api/admin/business-rooms';
        const params = new URLSearchParams();

        if (statusFilter) params.append('status', statusFilter);
        if (roomTypeFilter) params.append('roomType', roomTypeFilter);
        if (verificationFilter) params.append('verificationStatus', verificationFilter);
        if (searchTerm) params.append('search', searchTerm);

        const queryString = params.toString();
        return queryString ? `${url}?${queryString}` : url;
    };


    // 통계 데이터 로드
    const loadStats = async () => {
        try {
            // 실제 통계 API가 없으므로 임시 데이터 설정
            const dummyStats = {
                pending: 5,
                reviewing: 3,
                active: 12,
                verificationRate: '75%',
                total: 20
            };
            setStats(dummyStats);
        } catch (err) {
            console.error('통계 로딩 실패:', err);
            setError('통계 데이터를 불러오는데 실패했습니다.');
        }
    };

    // 방 목록 로드
    const loadRooms = async () => {
        setLoading(true);
        setError('');
        try {
            const url = getFilteredApiUrl();
            console.log('API 요청 URL:', url);

            const response = await axios.get(url);
            console.log('API 응답:', response.data);

            if (response.data.success) {
                // Transform the API data to match our component's interface
                const transformedRooms = response.data.data.map(transformApiData);
                console.log('변환된 데이터:', transformedRooms);
                setRooms(transformedRooms);
            } else {
                setError(response.data.message || '데이터 로드 중 오류가 발생했습니다.');
                setRooms([]);
            }
        } catch (err: any) {
            console.error('방 목록 로딩 실패:', err);
            setError('방 목록을 불러오는데 실패했습니다. 오류: ' + (err.message || JSON.stringify(err)));
            setRooms([]);
        } finally {
            setLoading(false);
        }
    };

    // 직접 데이터를 테스트용으로 로드
    const loadTestData = () => {
        setLoading(true);

        // const testData = [{
        //     "room_id": 50098,
        //     "title": "홍제동",
        //     "accommodation_type": "사업자 신고 완료 공간",
        //     "host_name": "관리자",
        //     "host_phone": "",
        //     "business_name": "ㅇㅇ",
        //     "business_number": "123456786",
        //     "business_representative": "123",
        //     "business_address": "서울특별시 서대문구 간호대로 3",
        //     "business_license_type": null,
        //     "business_license_file": "https://roomi.co.kr/image/room_business50098_1.jpg",
        //     "business_identification_file": null,
        //     "room_status": "PENDING",
        //     "room_type": "LODGE",
        //     "is_verified": false,
        //     "verified_at": null,
        //     "created_at": "2025-04-02T02:59:12.114Z",
        //     "room_status_label": "PENDING",
        //     "room_type_label": "LODGE",
        //     "verification_status": "미인증",
        //     "created_at_formatted": "2025-04-02",
        //     "verified_at_formatted": ""
        // }];

        // const transformedRooms = testData.map(room => transformApiData(room as ApiBusinessRoom));
        // setRooms(transformedRooms);
        setLoading(false);
        setError('');
    };

    // 방 목록을 직접 다시 불러오는 함수
    const refreshRooms = () => {
        loadRooms();
    };

    // 필터 변경시 데이터 다시 로드
    useEffect(() => {
        loadRooms();
    }, [statusFilter, roomTypeFilter, verificationFilter, searchTerm]);

    // 초기 로드
    useEffect(() => {
        loadStats();
        loadRooms();
    }, []);

    // 문서 모달 열기
    const openDocModal = (url: string, title: string) => {
        setDocImage(url);
        setDocTitle(title);
        setDocDialogOpen(true);
    };

    // 검색 핸들러
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // 필터 변경 핸들러
    const handleStatusChange = (e: any) => setStatusFilter(e.target.value);
    const handleRoomTypeChange = (e: any) => setRoomTypeFilter(e.target.value);
    const handleVerificationChange = (e: any) => setVerificationFilter(e.target.value);

    // 필터 리셋
    const resetFilters = () => {
        setStatusFilter('');
        setRoomTypeFilter('');
        setVerificationFilter('');
        setSearchTerm('');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5">인증 서류 심사</Typography>
                <Box>
                    <Button
                        variant="outlined"
                        onClick={refreshRooms}
                        startIcon={<Refresh />}
                        sx={{ mr: 2 }}
                    >
                        새로고침
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={loadTestData}
                        sx={{ mr: 2 }}
                    >
                        테스트 데이터
                    </Button>
                    <Button variant="contained" startIcon={<Add />}>새 심사 등록</Button>
                </Box>
            </Box>

            {/* 통계 카드 */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="textSecondary">대기중</Typography>
                            <Typography variant="h4" sx={{ color: 'warning.main' }}>{stats.pending}</Typography>
                            <Typography variant="caption">처리 필요</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="textSecondary">검토중</Typography>
                            <Typography variant="h4" sx={{ color: 'info.main' }}>{stats.reviewing}</Typography>
                            <Typography variant="caption">진행중</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="textSecondary">활성화</Typography>
                            <Typography variant="h4" sx={{ color: 'success.main' }}>{stats.active}</Typography>
                            <Typography variant="caption">양호</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="textSecondary">인증 완료율</Typography>
                            <Typography variant="h4">{stats.verificationRate}</Typography>
                            <Typography variant="caption">총 {stats.total}건</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* 필터 */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>상태</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={handleStatusChange}
                            label="상태"
                        >
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value="PENDING">대기중</MenuItem>
                            <MenuItem value="REVIEWING">검토중</MenuItem>
                            <MenuItem value="ACTIVE">활성화</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>숙소 유형</InputLabel>
                        <Select
                            value={roomTypeFilter}
                            onChange={handleRoomTypeChange}
                            label="숙소 유형"
                        >
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value="LODGE">LODGE</MenuItem>
                            <MenuItem value="ENTIRE">일반임대</MenuItem>
                            <MenuItem value="PRIVATE">공유민박</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>인증 상태</InputLabel>
                        <Select
                            value={verificationFilter}
                            onChange={handleVerificationChange}
                            label="인증 상태"
                        >
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value="인증완료">인증완료</MenuItem>
                            <MenuItem value="미인증">미인증</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="호스트명 또는 숙소명 검색"
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={resetFilters}
                        sx={{ height: '100%' }}
                    >
                        초기화
                    </Button>
                </Grid>
            </Grid>

            {/* 에러 메시지 */}
            {error && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* 테이블 */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>숙소명</TableCell>
                            <TableCell>호스트</TableCell>
                            <TableCell>사업자</TableCell>
                            <TableCell>유형</TableCell>
                            <TableCell>상태</TableCell>
                            {/*<TableCell>인증</TableCell>*/}
                            <TableCell>등록일</TableCell>
                            <TableCell>서류</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={24} />
                                    <Typography sx={{ ml: 2 }}>로딩 중...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : rooms.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <Typography>데이터가 없습니다.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rooms.map(room => (
                                <TableRow key={room.id} hover onClick={() => window.location.href = `/admin/business-rooms/${room.id}`} style={{ cursor: 'pointer' }}>
                                    <TableCell>{room.id}</TableCell>
                                    <TableCell>{room.title}</TableCell>
                                    <TableCell>
                                        <Typography>{room.hostName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{room.hostPhone || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>{room.businessName || '-'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{room.businessNumber || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{room.roomType}</TableCell>
                                    <TableCell>
                                        <Typography sx={{ color: statusBadgeColor(room.roomStatus) }}>
                                            {room.roomStatus === 'PENDING' ? '대기중' :
                                                room.roomStatus === 'REVIEWING' ? '검토중' :
                                                    room.roomStatus === 'ACTIVE' ? '활성화' : room.roomStatus}
                                        </Typography>
                                    </TableCell>
                                    {/*<TableCell>*/}
                                    {/*    {room.isVerified ? (*/}
                                    {/*        <Typography color="success.main">인증완료</Typography>*/}
                                    {/*    ) : (*/}
                                    {/*        <Typography color="warning.main">미인증</Typography>*/}
                                    {/*    )}*/}
                                    {/*</TableCell>*/}
                                    <TableCell>{room.createdAt}</TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        {room.businessLicenseFile ? (
                                            <IconButton onClick={(e) => {
                                                e.stopPropagation();
                                                openDocModal(room.businessLicenseFile!, '사업자등록증');
                                            }}>
                                                <Description />
                                            </IconButton>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">없음</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 서류 모달 */}
            <Dialog open={docDialogOpen} onClose={() => setDocDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{docTitle}</DialogTitle>
                <DialogContent>
                    <img src={docImage} alt={docTitle} style={{ width: '100%' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => window.open(docImage, '_blank')}>원본 보기</Button>
                    <Button onClick={() => setDocDialogOpen(false)}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BusinessRoomsList;